// src/components/appSettings/Collection/CollectionDetails.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiEdit2,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiPackage,
  FiTrendingUp,
  FiTarget,
  FiInfo,
  FiArrowLeft,
  FiStar,
  FiFilter,
  FiGrid,
  FiTag,
} from "react-icons/fi";
import {
  fetchCollectionByIdThunk,
  deleteCollectionThunk,
  updateCollectionStatusThunk,
  fetchCollectionProductsThunk,
} from "../../../store/thunks/appSettingsThunks";
import Button from "../../../components/common/Button";
import toast from "react-hot-toast";

const CollectionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedCollection, collectionProducts, loading } = useSelector(
    (state) => state.appSettings
  );
  const { branches } = useSelector((state) => state.branch);

  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (id) {
      dispatch(fetchCollectionByIdThunk(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (activeTab === "products" && id) {
      dispatch(fetchCollectionProductsThunk(id));
    }
  }, [activeTab, id, dispatch]);

  const handleDelete = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this collection? This action cannot be undone."
      )
    ) {
      try {
        await dispatch(deleteCollectionThunk(id)).unwrap();
        toast.success("Collection deleted successfully");
        navigate("/app-settings/collections");
      } catch (error) {
        toast.error(error.message || "Failed to delete collection");
      }
    }
  };

  const handleToggleStatus = async () => {
    try {
      await dispatch(
        updateCollectionStatusThunk({
          id,
          statusData: { isActive: !selectedCollection.isActive },
        })
      ).unwrap();
      toast.success(
        `Collection ${
          selectedCollection.isActive ? "deactivated" : "activated"
        } successfully`
      );
    } catch (error) {
      toast.error(error.message || "Failed to update status");
    }
  };

  const getBranchName = (branchCode) => {
    const branch = branches?.find((b) => b.code === branchCode);
    return branch ? `${branch.name} (${branchCode})` : branchCode;
  };

  const getStatusInfo = () => {
    if (!selectedCollection) return { text: "Unknown", color: "gray" };

    if (!selectedCollection.isActive) {
      return { text: "Inactive", color: "gray" };
    }

    const now = new Date();
    if (
      selectedCollection.validFrom &&
      new Date(selectedCollection.validFrom) > now
    ) {
      return { text: "Scheduled", color: "yellow" };
    }
    if (
      selectedCollection.validUntil &&
      new Date(selectedCollection.validUntil) < now
    ) {
      return { text: "Expired", color: "red" };
    }

    return { text: "Active", color: "green" };
  };

  if (loading || !selectedCollection) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statusInfo = getStatusInfo();

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/app-settings/collections")}
              >
                <FiArrowLeft className="mr-2" />
                Back to Collections
              </Button>
              <span
                className={`px-3 py-1 text-sm rounded-full bg-${statusInfo.color}-100 text-${statusInfo.color}-800`}
              >
                {statusInfo.text}
              </span>
              {selectedCollection.isFeatured && (
                <span className="inline-flex items-center px-3 py-1 text-sm rounded-full bg-yellow-100 text-yellow-800">
                  <FiStar className="mr-1" size={14} />
                  Featured
                </span>
              )}
              {selectedCollection.isExclusive && (
                <span className="px-3 py-1 text-sm rounded-full bg-purple-100 text-purple-800">
                  Exclusive
                </span>
              )}
            </div>

            <h1 className="text-2xl font-bold text-gray-900">
              {selectedCollection.name}
            </h1>
            {selectedCollection.shortDescription && (
              <p className="text-gray-600 mt-1">
                {selectedCollection.shortDescription}
              </p>
            )}
            <p className="text-sm text-gray-500 mt-1">
              Slug: {selectedCollection.slug}
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleToggleStatus}
              className={
                selectedCollection.isActive ? "text-red-600" : "text-green-600"
              }
            >
              {selectedCollection.isActive ? (
                <>
                  <FiEyeOff className="mr-2" />
                  Deactivate
                </>
              ) : (
                <>
                  <FiEye className="mr-2" />
                  Activate
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/app-settings/collections/edit/${id}`)}
            >
              <FiEdit2 className="mr-2" />
              Edit
            </Button>
            <Button
              variant="outline"
              onClick={handleDelete}
              className="text-red-600 hover:bg-red-50"
            >
              <FiTrash2 className="mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b">
          <div className="flex space-x-8 px-6">
            {["overview", "filters", "display", "products", "analytics"].map(
              (tab) => (
                <button
                  key={tab}
                  className={`py-3 border-b-2 font-medium text-sm capitalize ${
                    activeTab === tab
                      ? "border-primary text-primary"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              )
            )}
          </div>
        </div>

        <div className="p-6">
          {activeTab === "overview" && (
            <OverviewTab
              collection={selectedCollection}
              getBranchName={getBranchName}
            />
          )}
          {activeTab === "filters" && (
            <FiltersTab collection={selectedCollection} />
          )}
          {activeTab === "display" && (
            <DisplayTab collection={selectedCollection} />
          )}
          {activeTab === "products" && (
            <ProductsTab
              collection={selectedCollection}
              products={collectionProducts}
              loading={loading}
            />
          )}
          {activeTab === "analytics" && (
            <AnalyticsTab collection={selectedCollection} />
          )}
        </div>
      </div>
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ collection, getBranchName }) => (
  <div className="space-y-6">
    {/* Quick Stats */}
    {collection.analytics && (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          icon={FiEye}
          label="Views"
          value={collection.analytics.views || 0}
          color="blue"
        />
        <StatCard
          icon={FiTarget}
          label="Clicks"
          value={collection.analytics.clicks || 0}
          color="green"
        />
        <StatCard
          icon={FiPackage}
          label="Products"
          value={collection.productCount || 0}
          color="purple"
        />
        <StatCard
          icon={FiTrendingUp}
          label="Conversions"
          value={collection.analytics.conversions || 0}
          color="orange"
        />
      </div>
    )}

    {/* Basic Information */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <InfoSection title="Basic Details">
        <InfoRow label="Name" value={collection.name} />
        <InfoRow label="Slug" value={collection.slug} />
        {collection.shortDescription && (
          <InfoRow
            label="Short Description"
            value={collection.shortDescription}
          />
        )}
        <InfoRow
          label="Featured"
          value={collection.isFeatured ? "Yes" : "No"}
        />
        <InfoRow
          label="Exclusive"
          value={collection.isExclusive ? "Yes" : "No"}
        />
      </InfoSection>

      <InfoSection title="Display Settings">
        <InfoRow label="Priority" value={collection.display?.priority || 0} />
        {collection.display?.position && (
          <InfoRow label="Position" value={collection.display.position} />
        )}
        <InfoRow
          label="Card Style"
          value={collection.display?.cardStyle || "STANDARD"}
        />
        <InfoRow
          label="Show on Home"
          value={collection.display?.showOnHome ? "Yes" : "No"}
        />
        <InfoRow
          label="Show in Menu"
          value={collection.display?.showInMenu ? "Yes" : "No"}
        />
      </InfoSection>
    </div>

    {/* Description */}
    {collection.description && (
      <InfoSection title="Description">
        <p className="text-sm text-gray-700">{collection.description}</p>
      </InfoSection>
    )}

    {/* Metadata */}
    {(collection.metadata?.occasion ||
      collection.metadata?.season ||
      collection.metadata?.targetGender ||
      collection.metadata?.ageGroup) && (
      <InfoSection title="Metadata">
        <div className="grid grid-cols-2 gap-4">
          {collection.metadata.occasion && (
            <InfoRow label="Occasion" value={collection.metadata.occasion} />
          )}
          {collection.metadata.season && (
            <InfoRow label="Season" value={collection.metadata.season} />
          )}
          {collection.metadata.targetGender && (
            <InfoRow
              label="Target Gender"
              value={collection.metadata.targetGender}
            />
          )}
          {collection.metadata.ageGroup && (
            <InfoRow label="Age Group" value={collection.metadata.ageGroup} />
          )}
        </div>
      </InfoSection>
    )}

    {/* Targeting */}
    <InfoSection title="Targeting">
      {collection.targeting?.branches?.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {collection.targeting.branches.map((branchCode) => (
            <span
              key={branchCode}
              className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
            >
              {getBranchName(branchCode)}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">All branches</p>
      )}

      {(collection.validFrom || collection.validUntil) && (
        <div className="grid grid-cols-2 gap-4 mt-4">
          {collection.validFrom && (
            <InfoRow
              label="Valid From"
              value={new Date(collection.validFrom).toLocaleDateString()}
            />
          )}
          {collection.validUntil && (
            <InfoRow
              label="Valid Until"
              value={new Date(collection.validUntil).toLocaleDateString()}
            />
          )}
        </div>
      )}
    </InfoSection>

    {/* SEO */}
    {(collection.seo?.title ||
      collection.seo?.description ||
      collection.seo?.keywords?.length > 0) && (
      <InfoSection title="SEO Information">
        {collection.seo.title && (
          <InfoRow label="SEO Title" value={collection.seo.title} />
        )}
        {collection.seo.description && (
          <InfoRow label="SEO Description" value={collection.seo.description} />
        )}
        {collection.seo.keywords?.length > 0 && (
          <div className="flex items-start gap-2">
            <span className="text-sm text-gray-500 min-w-[100px]">
              Keywords:
            </span>
            <div className="flex flex-wrap gap-2">
              {collection.seo.keywords.map((keyword, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        )}
      </InfoSection>
    )}

    {/* Images Preview */}
    {(collection.images?.hero ||
      collection.images?.thumbnail ||
      collection.images?.banner ||
      collection.images?.icon) && (
      <InfoSection title="Images">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {collection.images.hero && (
            <ImagePreview label="Hero" url={collection.images.hero} />
          )}
          {collection.images.thumbnail && (
            <ImagePreview label="Thumbnail" url={collection.images.thumbnail} />
          )}
          {collection.images.banner && (
            <ImagePreview label="Banner" url={collection.images.banner} />
          )}
          {collection.images.icon && (
            <ImagePreview label="Icon" url={collection.images.icon} />
          )}
        </div>
      </InfoSection>
    )}
  </div>
);

// Filters Tab
const FiltersTab = ({ collection }) => (
  <div className="space-y-6">
    <InfoSection title="Product Filters">
      <p className="text-sm text-gray-500 mb-4">
        These filters determine which products are included in this collection
      </p>

      {/* Categories */}
      {collection.filters?.categories?.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Categories</h4>
          <div className="flex flex-wrap gap-2">
            {collection.filters.categories.map((category, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
              >
                {typeof category === "object" ? category.name : category}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Product Types */}
      {collection.filters?.productTypes?.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Product Types
          </h4>
          <div className="flex flex-wrap gap-2">
            {collection.filters.productTypes.map((type) => (
              <span
                key={type}
                className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
              >
                {type}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Metal Types */}
      {collection.filters?.metalTypes?.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Metal Types
          </h4>
          <div className="flex flex-wrap gap-2">
            {collection.filters.metalTypes.map((metal) => (
              <span
                key={metal}
                className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm"
              >
                {metal}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Purities */}
      {collection.filters?.purities?.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Purities</h4>
          <div className="flex flex-wrap gap-2">
            {collection.filters.purities.map((purity) => (
              <span
                key={purity}
                className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
              >
                {purity}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Price Range */}
      {(collection.filters?.priceRange?.min ||
        collection.filters?.priceRange?.max) && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Price Range
          </h4>
          <p className="text-sm text-gray-900">
            ₹{collection.filters.priceRange.min?.toLocaleString("en-IN") || 0} -
            ₹{collection.filters.priceRange.max?.toLocaleString("en-IN") || "∞"}
          </p>
        </div>
      )}

      {/* Tags */}
      {collection.filters?.tags?.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Tags</h4>
          <div className="flex flex-wrap gap-2">
            {collection.filters.tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* No Filters */}
      {!collection.filters?.categories?.length &&
        !collection.filters?.productTypes?.length &&
        !collection.filters?.metalTypes?.length &&
        !collection.filters?.purities?.length &&
        !collection.filters?.tags?.length &&
        !collection.filters?.priceRange?.min &&
        !collection.filters?.priceRange?.max && (
          <div className="text-center py-8">
            <FiFilter className="mx-auto text-4xl text-gray-400 mb-2" />
            <p className="text-gray-500">No filters applied</p>
            <p className="text-sm text-gray-400 mt-1">
              All products will be included
            </p>
          </div>
        )}
    </InfoSection>
  </div>
);

// Display Tab
const DisplayTab = ({ collection }) => (
  <div className="space-y-6">
    <InfoSection title="Display Configuration">
      <div className="grid grid-cols-2 gap-4">
        <InfoRow label="Priority" value={collection.display?.priority || 0} />
        <InfoRow
          label="Position"
          value={collection.display?.position || "Auto"}
        />
        <InfoRow
          label="Card Style"
          value={collection.display?.cardStyle || "STANDARD"}
        />
        <InfoRow
          label="Show on Home"
          value={collection.display?.showOnHome ? "Yes" : "No"}
        />
        <InfoRow
          label="Show in Menu"
          value={collection.display?.showInMenu ? "Yes" : "No"}
        />
      </div>
    </InfoSection>

    {/* Color Settings */}
    {(collection.display?.textColor ||
      collection.display?.badgeText ||
      collection.display?.badgeColor) && (
      <InfoSection title="Color & Badge Settings">
        <div className="space-y-3">
          {collection.display.textColor && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 w-32">Text Color:</span>
              <div
                className="w-8 h-8 rounded border"
                style={{ backgroundColor: collection.display.textColor }}
              />
              <span className="text-sm font-mono">
                {collection.display.textColor}
              </span>
            </div>
          )}

          {collection.display.badgeText && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 w-32">Badge:</span>
              <span
                className="px-3 py-1 rounded text-sm font-medium"
                style={{
                  backgroundColor: collection.display.badgeColor || "#FF0000",
                  color: "#FFFFFF",
                }}
              >
                {collection.display.badgeText}
              </span>
            </div>
          )}
        </div>
      </InfoSection>
    )}

    {/* Preview */}
    <InfoSection title="Card Preview">
      <div className="flex justify-center">
        <div className="w-64">
          <CollectionCardPreview collection={collection} />
        </div>
      </div>
    </InfoSection>
  </div>
);

// Products Tab
const ProductsTab = ({ collection, products, loading }) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">
          Products in this Collection ({products?.length || 0})
        </h3>
        <Button variant="outline" size="sm">
          <FiGrid className="mr-2" />
          View All Products
        </Button>
      </div>

      {products && products.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {products.map((product) => (
            <ProductCard key={product._id} product={product} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <FiPackage className="mx-auto text-4xl text-gray-400 mb-2" />
          <p className="text-gray-500">No products found in this collection</p>
          <p className="text-sm text-gray-400 mt-1">
            Products matching the filters will appear here
          </p>
        </div>
      )}
    </div>
  );
};

// Analytics Tab
const AnalyticsTab = ({ collection }) => {
  const data = collection.analytics || {};

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          label="Total Views"
          value={data.views || 0}
          icon={FiEye}
          color="blue"
        />
        <MetricCard
          label="Total Clicks"
          value={data.clicks || 0}
          icon={FiTarget}
          color="green"
        />
        <MetricCard
          label="Conversions"
          value={data.conversions || 0}
          icon={FiTrendingUp}
          color="purple"
        />
        <MetricCard
          label="CTR"
          value={
            data.views > 0
              ? `${((data.clicks / data.views) * 100).toFixed(2)}%`
              : "0%"
          }
          icon={FiTrendingUp}
          color="orange"
        />
      </div>

      {!data.views && !data.clicks && (
        <div className="text-center py-12">
          <FiInfo className="mx-auto text-4xl text-gray-400 mb-2" />
          <p className="text-gray-500">
            No analytics data available yet. Data will appear once users
            interact with this collection.
          </p>
        </div>
      )}
    </div>
  );
};

// Helper Components
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div
    className={`bg-gradient-to-br from-${color}-50 to-${color}-100 rounded-lg p-4`}
  >
    <div className="flex items-center justify-between mb-2">
      <Icon className={`text-2xl text-${color}-600`} />
    </div>
    <p className="text-sm text-gray-600">{label}</p>
    <p className={`text-2xl font-bold text-${color}-700`}>{value}</p>
  </div>
);

const MetricCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white border rounded-lg p-4">
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`text-${color}-600`} />
      <span className="text-sm text-gray-600">{label}</span>
    </div>
    <p className={`text-2xl font-bold text-${color}-700`}>{value}</p>
  </div>
);

const InfoSection = ({ title, children }) => (
  <div className="bg-white border rounded-lg p-4">
    <h3 className="font-medium text-gray-900 mb-3">{title}</h3>
    <div className="space-y-2">{children}</div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-gray-500">{label}:</span>
    <span className="text-sm font-medium text-gray-900">{value}</span>
  </div>
);

const ImagePreview = ({ label, url }) => (
  <div>
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <div className="border rounded-lg overflow-hidden bg-gray-50">
      <img src={url} alt={label} className="w-full h-24 object-cover" />
    </div>
  </div>
);

const CollectionCardPreview = ({ collection }) => (
  <div className="bg-white border rounded-lg overflow-hidden shadow-md">
    {collection.images?.thumbnail && (
      <img
        src={collection.images.thumbnail}
        alt={collection.name}
        className="w-full h-40 object-cover"
      />
    )}
    <div className="p-4">
      <h4 className="font-medium text-gray-900">{collection.name}</h4>
      {collection.shortDescription && (
        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
          {collection.shortDescription}
        </p>
      )}
      {collection.display?.badgeText && (
        <span
          className="inline-block px-2 py-1 text-xs rounded mt-2"
          style={{
            backgroundColor: collection.display.badgeColor || "#FF0000",
            color: "#FFFFFF",
          }}
        >
          {collection.display.badgeText}
        </span>
      )}
    </div>
  </div>
);

const ProductCard = ({ product }) => (
  <div className="bg-white border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
    <div className="h-40 bg-gray-200">
      {product.media?.[0] && (
        <img
          src={product.media[0].url}
          alt={product.name}
          className="w-full h-full object-cover"
        />
      )}
    </div>
    <div className="p-3">
      <h4 className="font-medium text-sm text-gray-900 line-clamp-1">
        {product.name}
      </h4>
      <p className="text-xs text-gray-500 mt-1">ID: {product.productId}</p>
      <div className="flex items-center justify-between mt-2">
        <span className="text-sm text-gray-600">
          {product.type} • {product.subType}
        </span>
        <span className="text-xs text-gray-500">{product.grossWeight}g</span>
      </div>
    </div>
  </div>
);

export default CollectionDetails;
